<template>
    <jet-section>
        <template #title>
            Balance
        </template>
        <template #description>
            This is your current balance and the sum of all your wins that you have yet to withdraw.
        </template>
        <template #content v-if="user.is_admin">
            <dl class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div class="relative dark:bg-gray-900 bg-white pt-5 px-4 pb-5 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden">
                    <dt>
                        <div class="absolute bg-racing-stars-yellow rounded-md p-3">
                            <CashIcon class="h-6 w-6 text-gray-800" aria-hidden="true" />
                        </div>
                        <p class="ml-16 text-sm font-medium text-gray-500 dark:text-gray-200 truncate">Current Balance</p>
                    </dt>
                    <dd class="ml-16 flex items-baseline">
                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ formatter.format(user.balance / 100) }}
                        </p>
                    </dd>
                </div>

                <div class="relative dark:bg-gray-900 bg-white pt-5 px-4 pb-5 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden">
                    <dt>
                        <div class="absolute bg-racing-stars-yellow rounded-md p-3">
                            <SwitchVerticalIcon class="h-6 w-6 text-gray-800" aria-hidden="true" />
                        </div>
                        <p class="ml-16 text-sm font-medium text-gray-500 dark:text-gray-200 truncate">Amt. Withdrawn</p>
                    </dt>
                    <dd class="ml-16 flex items-baseline">
                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ formatter.format(withdrawals) }}
                        </p>
                    </dd>
                </div>

                <div class="relative dark:bg-gray-900 bg-white pt-5 px-4 pb-5 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden">
                    <dt>
                        <div class="absolute bg-racing-stars-yellow rounded-md p-3">
                            <CurrencyPoundIcon class="h-6 w-6 text-gray-800" aria-hidden="true" />
                        </div>
                        <p class="ml-16 text-sm font-medium text-gray-500 dark:text-gray-200 truncate">Winnings</p>
                    </dt>
                    <dd class="ml-16 flex items-baseline">
                        <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ formatter.format(winnings) }}
                        </p>
                    </dd>
                </div>

            </dl>
        </template>
        <template v-if="user.balance && user.is_admin" #actions>
            <span v-if="user.balance < 1000" class="text-racing-stars-orange">You need a minimum balance of at least £10.00 to withdraw</span>
            <jet-button v-if="user.balance >= 1000" @click="withdrawalPopupOpen = true">
                Withdraw
            </jet-button>
        </template>
    </jet-section>
    <jet-modal :show="withdrawalPopupOpen" @close="withdrawalPopupOpen = false">
        <jet-form-section>
            <template #form>
                <!-- Amount -->
                <div class="col-span-12 mb-2">
                    <jet-label for="amount" value="Withdrawal Amount (£)" />
                    <jet-input id="amount" type="text" class="mt-1 block w-full" v-model="withdrawalForm.amount" />
                    <jet-input-error :message="withdrawalForm.errors.amount" class="mt-2" />
                    <span v-if="withdrawalForm.amount < 10" class="mt-2 text-racing-stars-orange" >Please enter an amount greater than £10.</span>
                </div>
                <!-- Card Number -->
                <div class="col-span-12 mb-2">
                    <jet-label for="card_number" value="Card Number" />
                    <jet-input id="card_number" type="text" class="mt-1 block w-full" v-model="withdrawalForm.card_number" autocomplete="cc-number" placeholder="1234 1234 1234 1234" v-cardformat:formatCardNumber />
                    <jet-input-error :message="withdrawalForm.errors.card_number" class="mt-2" />
                </div>
                <div class="mb-6 grid grid-cols-2 gap-2 pb-4 pt-4">
                    <!-- Expiry Date -->
                    <div class="col-span-1">
                        <jet-label for="expiry_date" value="Expiry Date" />
                        <jet-input id="expiry_date" type="text" class="mt-1 block w-full" v-model="withdrawalForm.expiry_date" autocomplete="cc-exp" placeholder="MM/DD" v-cardformat:formatCardExpiry />
                        <jet-input-error :message="withdrawalForm.errors.expiry_date" class="mt-2" />
                    </div>
                    <!-- CSV -->
                    <div class="col-span-1">
                        <jet-label for="csv" value="CVC Code" />
                        <jet-input id="csv" type="text" class="mt-1 block w-full" v-model="withdrawalForm.csv" autocomplete="cc-csc" placeholder="123" v-cardformat:formatCardCVC />
                        <jet-input-error :message="withdrawalForm.errors.csv" class="mt-2" />
                    </div>
                </div>
            </template>
            <template #actions>
                <tw-button :sm="true" :secondary="true" class="mr-2" @click="withdrawalPopupOpen = false">
                    Close
                </tw-button>
                <tw-button v-if="withdrawalForm.amount >= 10" :class="{ 'opacity-25': withdrawalForm.processing }" :disabled="withdrawalForm.processing" @click="withdrawFunds">
                    Withdraw
                </tw-button>
            </template>
        </jet-form-section>
    </jet-modal>
</template>
<script>
import { ref, computed, getCurrentInstance } from 'vue'
import JetSection from '@/Jetstream/Section.vue'
import JetButton from '@/Jetstream/Button.vue'
import JetModal from '@/Jetstream/Modal.vue'
import JetFormSection from '@/Jetstream/FormSection.vue'
import JetInput from '@/Jetstream/Input.vue'
import JetLabel from '@/Jetstream/Label.vue'
import JetInputError from '@/Jetstream/InputError.vue'
import JetActionMessage from '@/Jetstream/ActionMessage.vue'
import TwButton from '@/Global/Elements/TwButton.vue'
import { CurrencyPoundIcon, TrendingUpIcon, TrendingDownIcon, SwitchVerticalIcon, SwitchHorizontalIcon, CreditCardIcon, CashIcon } from '@heroicons/vue/outline'
import { Inertia } from '@inertiajs/inertia'

export default {
    components: {
        JetSection,
        JetButton,
        JetModal,
        JetFormSection,
        JetInput,
        JetLabel,
        JetInputError,
        TwButton,
        TrendingUpIcon,
        TrendingDownIcon,
        SwitchVerticalIcon,
        SwitchHorizontalIcon,
        CreditCardIcon,
        CashIcon,
        CurrencyPoundIcon,
    },

    props: {
        user: Object,
        errors: Object,
    },

    setup(props) {

        /**
         * Formatter function required to format currencies/prices correctly.
         *
         * @type {Intl}
         */
        const formatter = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
        })

        const withdrawalPopupOpen = ref(false)
        const withdrawalSuccessful = ref(false)

        const withdrawFunds = () => {
            withdrawalForm
                .transform((data) => ({
                    ...data,
                    amount: data.amount * 100,
                }))
                .post(route('payments.store'), {
                    onSuccess: () => {
                        withdrawalForm.reset()
                        withdrawalPopupOpen.value = false
                        Inertia.reload({ only: ['user'] })
                        withdrawalSuccessful.value = true
                    },

                    onError: (e) => {
                        withdrawalSuccessful.value = false
                    }
                })
        }

        const withdrawalForm = Inertia.form({
            amount: '10',
            card_number: null,
            expiry_date: null,
            csv: null,
            type: 'payout',
        })

        const deposits = computed(() => {
            let depositsTotal = 0

            props.user.payments.forEach(payment => {
                if (payment.type === 'CHARGE') {
                    depositsTotal = parseInt(depositsTotal) + parseInt(payment.amount)
                }
            })

            return (depositsTotal / 100).toFixed(2)
        })

        const withdrawals = computed(() => {
            let withdrawalTotal = 0

            props.user.payments.forEach(payment => {
                if (payment.type === 'PAYOUT') {
                    withdrawalTotal = parseInt(withdrawalTotal) + parseInt(payment.amount)
                }
            })

            return (withdrawalTotal / 100).toFixed(2)
        })

        const winnings = computed(() => {
            let winningsTotal = 0

            props.user.bets.forEach(bet => {
                if (bet.amount > 0) {
                    winningsTotal = parseInt(winningsTotal) + parseInt(bet.amount)
                }
            })

            return (winningsTotal / 100).toFixed(2)
        })

        const profitLoss = computed(() => {
            return parseFloat(winnings.value - deposits.value).toFixed(2)
        })

        return {
            formatter,
            deposits,
            withdrawals,
            profitLoss,
            withdrawalPopupOpen,
            withdrawalForm,
            withdrawalSuccessful,
            withdrawFunds,
            winnings,
        }
    }
}

</script>
